import { Flex, Stack } from '@chakra-ui/react';
import { isUndefined } from 'lodash';
import React, { useEffect, useState } from 'react';

import { TextField } from 'Containers/App/types';
import { hideAccidentPremiums } from 'Containers/App/utils';
import { CUSTOM_NAME_PLAN_TYPES } from 'Containers/OverviewPage/constants';
import { ContentfulCustomMessage } from 'ContentfulDefaults/types/_shared';
import { ContentfulSupplementalPlan } from 'ContentfulDefaults/types/benefitsAdditional';
import { useTextContext } from 'Contexts/textContext';
import { FadeUp, H4, H6, Icon, Paragraph } from 'DesignLibrary/atoms';
import { PremiumPerDeduction } from 'DesignLibrary/atoms/PremiumPerDeduction';
import { SelectedBadge } from 'DesignLibrary/atoms/SelectedBadge';
import { useViewport } from 'DesignLibrary/context';
import { TooltipPlanDetails } from 'DesignLibrary/molecules';
import InfoBar from 'DesignLibrary/molecules/InfoBar';
import RichText from 'Shared/RichText';
import Text from 'Shared/Text';
import {
  CoveredMember,
  DollarField,
  DollarRangeField,
  HouseholdMember,
  SupplementalPlan,
} from 'Types/entities';
import { BuilderGoSettings } from 'Utils/apiTypes';
import { sendInsightsEvent } from 'Utils/insights';

import PlanButtons from './PlanButtons';
import { CONTENT_MAP } from '../constants';
import { InsuranceType } from '../types';
import PlanContent from './PlanContent/planContent';
import { ButtonContainer, CardHeader, PlanCard } from './styled';

export interface ExpandablePlanCardProps {
  plan: SupplementalPlan;
  contentfulSupplementalPlan?: ContentfulSupplementalPlan;
  isSelected: boolean;
  insuranceType: InsuranceType;
  idx: number;
  isNonSelectableBenefit: boolean;
  coveredMembers: {
    [memberId: string]: CoveredMember;
  };
  showFbsCardDetails: boolean;
  planWithFullHouseholdSelected: SupplementalPlan | Record<string, never>;
  selectablePlanCount: number;
  addCarrier?: string;
  builderCustomerKey: string | null;
  employerName: string;
  selectPlan: (plan: SupplementalPlan) => void;
  removePlan: () => void;
  householdMembers: HouseholdMember[];
  builderGoSettings: BuilderGoSettings | null;
  isLimitedPurposeFsa?: boolean;
}

const ExpandablePlanCard = ({
  plan,
  contentfulSupplementalPlan,
  isSelected,
  insuranceType,
  idx,
  isNonSelectableBenefit,
  coveredMembers,
  showFbsCardDetails,
  planWithFullHouseholdSelected,
  selectablePlanCount,
  addCarrier,
  builderCustomerKey,
  employerName,
  selectPlan,
  removePlan,
  householdMembers,
  builderGoSettings,
  isLimitedPurposeFsa: isLimitedPurposeFsaProp = false,
}: ExpandablePlanCardProps) => {
  const { device } = useViewport();
  const [warningText, setWarningText] = useState<TextField | ''>('');
  const { retrieveContentfulData } = useTextContext();

  const isMoreSelectablePlans = selectablePlanCount > 0;

  const isAutoEnrolled = plan.auto_enrolled;
  const isEligible =
    isUndefined(plan.ineligible_members) || isUndefined(coveredMembers)
      ? true
      : plan.ineligible_members && plan.ineligible_members.length === 0;

  const isEmployerPaid = contentfulSupplementalPlan?.is_employer_paid;
  const isLimitedPurposeFsa =
    isLimitedPurposeFsaProp && (insuranceType === 'fsa' || insuranceType === 'fsa_medical');
  const benefitTitle =
    (CUSTOM_NAME_PLAN_TYPES.includes(insuranceType)
      ? plan.name
      : retrieveContentfulData<string>(
          isLimitedPurposeFsa
            ? `${CONTENT_MAP[insuranceType].field}.alternate_title`
            : `${CONTENT_MAP[insuranceType].field}.card_title`,
        )) || insuranceType;
  useEffect(() => {
    if (!isNonSelectableBenefit) {
      updateWarningText();
    }
  }, [planWithFullHouseholdSelected]);

  const updateWarningText = () => {
    let hasSpouse = false;
    let hasDependents = false;
    let ineligibleDependents = false;
    let ineligibleSpouse = false;

    const eligibleMemberIds = planWithFullHouseholdSelected?.eligible_members || [];
    Object.entries(coveredMembers).forEach(([key, value]) => {
      if (value.dependant) {
        hasDependents = true;
        if (!eligibleMemberIds.includes(key)) {
          ineligibleDependents = true;
        }
      } else if (!value.policyholder) {
        hasSpouse = true;
        if (!eligibleMemberIds.includes(key)) {
          ineligibleSpouse = true;
        }
      }
    });
    let warning: TextField | '' = ``;
    if (hasSpouse && hasDependents) {
      if (ineligibleSpouse && ineligibleDependents) {
        warning = `benefits_section.plan_card_text.notEligibleWarning.yourself`;
      } else if (ineligibleSpouse) {
        warning = `benefits_section.plan_card_text.notEligibleWarning.youAndDependents`;
      } else if (ineligibleDependents) {
        warning = `benefits_section.plan_card_text.notEligibleWarning.youAndSpouse`;
      }
    } else if (hasSpouse) {
      if (ineligibleSpouse) {
        warning = `benefits_section.plan_card_text.notEligibleWarning.yourself`;
      }
    } else if (hasDependents) {
      if (ineligibleDependents) {
        warning = `benefits_section.plan_card_text.notEligibleWarning.yourself`;
      }
    }
    setWarningText(warning);
  };

  const handleSelect = (e) => {
    const coveredMembersIds = Object.keys(coveredMembers)
      .map((externalId) => coveredMembers[externalId])
      .filter((member) => member.covered === true)
      .map((member) => member.memberId);

    sendInsightsEvent(e, 'choose_supplemental_plan', {
      covered_members: coveredMembersIds,
      plan_type: insuranceType,
      plan,
    });

    selectPlan(plan);
  };

  const handleTrackPdfView = (e) => {
    sendInsightsEvent(e, 'view_supplemental_plan_pdf', {
      plan_type: insuranceType,
      plan,
    });
  };

  const renderCustomMessage = () => {
    const contentfulSupplementalPlans = retrieveContentfulData<ContentfulSupplementalPlan[]>(
      'benefits_section_additional.supplemental_plans',
      [],
    );

    const customMessageIdx = contentfulSupplementalPlans.findIndex((i) => i.plan_id === plan.external_id);
    if (customMessageIdx >= 0) {
      const customMessage = contentfulSupplementalPlans[customMessageIdx]
        .custom_message as ContentfulCustomMessage;

      if (!customMessage) {
        return null;
      }
      return (
        <InfoBar
          className={`${plan.external_id}-customMessage`}
          text={
            <RichText
              inlineParagraphs
              field={
                `benefits_section_additional.supplemental_plans[${customMessageIdx}].custom_message.text` as TextField
              }
            />
          }
          color={customMessage?.color}
          header={customMessage?.header}
          stretch
          icon={customMessage?.icon}
          buttons={[{ text: customMessage?.url_button_text, url: customMessage?.url }]}
        />
      );
    }
    return null;
  };

  const parsePremiumFromPlan = (planPremium: DollarRangeField | DollarField): number => {
    if (typeof planPremium === 'object') {
      if (planPremium.type === 'USD') {
        return planPremium.data;
      }

      return planPremium.data.lower;
    }

    return planPremium;
  };

  const getPremiumComponent = () => {
    if (hideAccidentPremiums(plan, builderGoSettings)) {
      return undefined;
    }

    const planTypesWithVaryingPremiums = ['pet', 'cancer', 'ltc'];

    if (planTypesWithVaryingPremiums.includes(plan.plan_type)) {
      const costVariesTooltipFields = {
        pet: 'benefits_section_pet.cost_varies_tooltip',
        cancer: 'benefits_section_cancer.deductible_tooltip',
        ltc: 'benefits_section_ltc.cost_varies_tooltip',
      };
      return (
        <TooltipPlanDetails
          tooltip={{
            text: retrieveContentfulData<string>(costVariesTooltipFields[plan.plan_type]) || '',
          }}
          keyHint={`cost-varies-tooltip-${plan.external_id}`}
        >
          <Text field="benefits_section_additional.cost_varies_label" />
        </TooltipPlanDetails>
      );
    }
    if (plan.monthly_premium) {
      const planPremium = parsePremiumFromPlan(plan.monthly_premium);
      return (
        <PremiumPerDeduction
          annualPremium={planPremium * 12}
          deductions={plan.deductions_per_year}
          currency
        />
      );
    }

    return undefined;
  };

  const showBundledWithMedical = () => {
    if (plan.benefits.items.find((i) => i.item.data === 'plan_in_medical')?.description.data === 'true') {
      return (
        <InfoBar
          text={<Text field="benefits_section_vision.bundled_with_medical" />}
          showAccent={false}
          size="sm"
        />
      );
    }
    return null;
  };

  const setCarrierName = () => {
    if (insuranceType === 'add') {
      return addCarrier;
    }
    if (insuranceType === 'fsa_dependent') {
      return '';
    }
    return contentfulSupplementalPlan?.carrier || plan.carrier_name;
  };

  return (
    <PlanCard className="expandable-plan-card" isSelected={isSelected} isAutoEnrolled={isAutoEnrolled}>
      <div className="badge-container">
        <div className="badge">
          <FadeUp translateX="1rem" translateY="0" timeout={500} isOpen={device === 'desktop' && isSelected}>
            <SelectedBadge type={isAutoEnrolled ? 'auto_enrolled' : 'selected'} />
          </FadeUp>
        </div>
      </div>

      <CardHeader
        className={`plan-head${['fsa', 'fsa_medical', 'fsa_dependent'].includes(insuranceType) ? ' with-separator' : ''}`}
      >
        <div className="header">
          <FadeUp isOpen={device !== 'desktop' && isSelected}>
            <SelectedBadge
              type={isAutoEnrolled ? 'auto_enrolled' : 'selected'}
              sizeOverride={device === 'mobile' ? 'small' : 'large'}
            />
          </FadeUp>
          <div className={`header-text ${isSelected ? 'scoot' : null}`} id={`${plan.external_id}-title`}>
            <Paragraph size="small" className="carrier-name">
              {setCarrierName()}
            </Paragraph>
            <H4 as="h3" className="plan-name">
              {benefitTitle}
            </H4>
          </div>
        </div>
        <Stack gap={2} align="flex-end">
          {showBundledWithMedical()}
          <div className="premium">{getPremiumComponent()}</div>
        </Stack>
      </CardHeader>

      {(!isEligible || isAutoEnrolled || isEmployerPaid) && (
        <section className="messages">
          {!isEligible && !isAutoEnrolled && (
            <InfoBar text={<Text data-testid="ineligible-warning" field={warningText as TextField} />} />
          )}
          {isEmployerPaid && (
            <InfoBar
              text={
                <RichText
                  inlineParagraphs
                  field="benefits_section_additional.fbs_plan_auto_enrolled_info_bar.text"
                />
              }
              color="blue"
              iconOverride={<Icon type="CheckCircle" color="--primary-blue" />}
              showAccent={false}
            />
          )}
          {isAutoEnrolled && isMoreSelectablePlans && (
            <InfoBar
              text={
                <RichText
                  inlineParagraphs
                  field="benefits_section_additional.fbs_plan_buy_up_info_bar.text"
                />
              }
              color="blue"
              iconOverride={<Icon type="PlusCircle" color="--primary-blue" />}
              showAccent={false}
            />
          )}
        </section>
      )}

      {renderCustomMessage()}

      <section className="plan-content">
        {insuranceType === 'dental' && (
          <Flex mb={2}>
            <H6 className="plan-name">
              <Text field="benefits_section_additional.in_network" />
            </H6>
          </Flex>
        )}

        <PlanContent
          plan={plan}
          contentfulSupplementalPlan={contentfulSupplementalPlan}
          showFbsCardDetails={showFbsCardDetails}
          employerName={employerName}
          builderCustomerKey={builderCustomerKey}
          householdMembers={householdMembers}
          isLimitedPurposeFsa={isLimitedPurposeFsa}
        />
      </section>

      {(!isNonSelectableBenefit || plan.pdf_url) && (
        <ButtonContainer className="button-container">
          <PlanButtons
            plan={plan}
            idx={idx}
            insuranceType={insuranceType}
            isSelected={isSelected}
            isAutoEnrolled={isAutoEnrolled}
            isEligible={isEligible}
            hasNoSelection={isNonSelectableBenefit}
            handleSelect={handleSelect}
            handleTrackPdfView={handleTrackPdfView}
            handleRemovePlan={removePlan}
          />
        </ButtonContainer>
      )}
    </PlanCard>
  );
};

export default ExpandablePlanCard;
